import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';

// Which import surface the rendered snippets target. The on-chain calls and
// returned ix shape are identical between the two — only the import paths
// (and the type of the returned instruction) differ.
//
//   kit    → import from '@sly-rentals/core'        (returns @solana/kit ix)
//   web3js → import from '@sly-rentals/core/legacy' (returns web3.js ix array)
export type CodeFlavor = 'kit' | 'web3js';

const STORAGE_KEY = 'playground:code-flavor';

interface CodeFlavorContextValue {
  flavor: CodeFlavor;
  setFlavor: (next: CodeFlavor) => void;
}

const CodeFlavorContext = createContext<CodeFlavorContextValue | null>(null);

function readPersisted(): CodeFlavor {
  if (typeof window === 'undefined') return 'kit';
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'kit' || raw === 'web3js') return raw;
  } catch {
    /* fall through */
  }
  return 'kit';
}

export function CodeFlavorProvider({ children }: { children: ReactNode }) {
  const [flavor, setFlavorState] = useState<CodeFlavor>(readPersisted);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, flavor);
    } catch {
      /* private browsing — silent */
    }
  }, [flavor]);

  const setFlavor = useCallback((next: CodeFlavor) => setFlavorState(next), []);

  return (
    <CodeFlavorContext.Provider value={{ flavor, setFlavor }}>
      {children}
    </CodeFlavorContext.Provider>
  );
}

export function useCodeFlavor(): CodeFlavorContextValue {
  const ctx = useContext(CodeFlavorContext);
  if (!ctx) throw new Error('useCodeFlavor must be used inside <CodeFlavorProvider>');
  return ctx;
}
